import vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import { getVtpBounds, zoomToElement } from '../../meshes/queries/mesh-queries-utils';
import { ILineSegment, IViewerBounds } from '../../MikeVisualizer/lib/IMikeVisualizerModels';
import MikeVisualizerLib from '../../MikeVisualizer/lib/MikeVisualizer';
import { REPRESENTATION } from '../../MikeVisualizer/lib/MikeVisualizerConstants';
import { IWorkspace } from '../../models/IWorkspaces';
import { EWorkspaceMeshActionType } from '../../store/actions/WorkspaceMeshActionType';
import { store } from '../../store/store';
import { MB_MAP_COLORS } from '../../workspaces/viewer/viewer-utils';

const ELEMENT_LAYER = 'ElementLayer';
const SEGMENT_MARKER_LAYER = 'SegmentMarkerLayer';
const SEGMENT_MARKER_POINT_SIZE = 12 as const;

const SEGMENT_MARKER_MAX_LENGTH = {
  latLon: 0.1,
  metric: 0.01,
};

const ZOOM_SEGMENT_HIGHLIGHT_COLOR = {
  edge: [...MB_MAP_COLORS.POPPYGREEN, 1],
  surface: [...MB_MAP_COLORS.POPPYGREEN, 1],
};

const GEOMETRY_SEGMENT_FALLBACK_REPRESENTATION = REPRESENTATION.SURFACE;
const ZOOM_SEGMENT_PADDING_FACTOR = 1 as const;

const MIN_SEGMENT_LENGTH_FOR_SEGMENT_ZOOM = 0.01 as const;
const MIN_SEGMENT_ZOOM_PADDING = 0.1 as const;
const METRES_PER_DEGREE = 111320 as const;

interface IClosestGeometrySegment {
  segment: ILineSegment;
  distance: number;
}

const createMarkerPolyData = (point: Array<number>) => {
  const polyData = vtkPolyData.newInstance();
  polyData.getPoints().setData(Float64Array.from(point), 3);
  polyData.getVerts().setData(Uint32Array.from([1, 0]));
  return polyData;
};

const drawSegmentMarker = (point: Array<number>) => {
  MikeVisualizerLib.updateData(
    createMarkerPolyData(point),
    SEGMENT_MARKER_LAYER,
    ZOOM_SEGMENT_HIGHLIGHT_COLOR.edge,
    ZOOM_SEGMENT_HIGHLIGHT_COLOR.surface,
    REPRESENTATION.POINTS,
    SEGMENT_MARKER_POINT_SIZE,
  );
};

const findMatchingGeometrySegment = (geometryId: string, vtp: string) => {
  const bounds = getVtpBounds(vtp);
  const geometrySegments = MikeVisualizerLib.getLineSegments(geometryId);

  if (!bounds || !bounds.isValid || !geometrySegments || !geometrySegments.length) {
    return null;
  }

  const boundsCenterX = (bounds.minX + bounds.maxX) / 2;
  const boundsCenterY = (bounds.minY + bounds.maxY) / 2;

  const closestSegment = geometrySegments.reduce<IClosestGeometrySegment | null>((currentClosest, segment) => {
    const midX = (segment.start[0] + segment.end[0]) / 2;
    const midY = (segment.start[1] + segment.end[1]) / 2;
    const distance = Math.hypot(midX - boundsCenterX, midY - boundsCenterY);

    if (!currentClosest || distance < currentClosest.distance) {
      return { segment, distance };
    }
    return currentClosest;
  }, null);

  return closestSegment?.segment ?? null;
};

/**
 * Highlights the queried segment: the real geometry segment when found (e.g. for lines),
 * otherwise the approximate queried shape (e.g. for polygons). Very short segments also get a marker.
 */
export const renderGeometrySegment = (geometryId: string, vtp: string, length: number, workspace: IWorkspace) => {
  const markerMaxLength = workspace.isLatLon ? SEGMENT_MARKER_MAX_LENGTH.latLon : SEGMENT_MARKER_MAX_LENGTH.metric;
  const showMarker = length < markerMaxLength;
  const foundGeometrySegment = findMatchingGeometrySegment(geometryId, vtp);
  MikeVisualizerLib.deleteData(SEGMENT_MARKER_LAYER);

  if (foundGeometrySegment) {
    MikeVisualizerLib.deleteData(ELEMENT_LAYER);
    MikeVisualizerLib.highlightSegmentCell({
      elementId: geometryId,
      segmentIndex: foundGeometrySegment.segmentIndex,
      highlightColor: ZOOM_SEGMENT_HIGHLIGHT_COLOR.surface,
    });
    showMarker && drawSegmentMarker(foundGeometrySegment.start);
    return;
  }

  MikeVisualizerLib.clearSegmentCellHighlight(geometryId);
  if (showMarker) {
    const bounds = getVtpBounds(vtp);
    drawSegmentMarker([(bounds.minX + bounds.maxX) / 2, (bounds.minY + bounds.maxY) / 2, 0]);
  }
  MikeVisualizerLib.updateData(
    vtp,
    ELEMENT_LAYER,
    ZOOM_SEGMENT_HIGHLIGHT_COLOR.edge,
    ZOOM_SEGMENT_HIGHLIGHT_COLOR.surface,
    GEOMETRY_SEGMENT_FALLBACK_REPRESENTATION,
  );
};

export const clearGeometrySegmentHighlight = (geometryId: string) => {
  MikeVisualizerLib.deleteData(ELEMENT_LAYER);
  MikeVisualizerLib.deleteData(SEGMENT_MARKER_LAYER);
  MikeVisualizerLib.clearSegmentCellHighlight(geometryId);
};

/**
 * Zooms to the queried segment with padding proportional to its length (in metres).
 * Very short segments use the generic buffered zoom, as zooming in closer makes the geometry disappear.
 */
export const zoomToGeometrySegment = (vtp: string, length: number, workspace: IWorkspace) => {
  if (length < MIN_SEGMENT_LENGTH_FOR_SEGMENT_ZOOM) {
    zoomToElement(vtp, length, workspace);
    return;
  }

  const bounds = getVtpBounds(vtp);
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;
  const toWorkspaceUnits = (metres: number) => (workspace.isLatLon ? metres / METRES_PER_DEGREE : metres);
  const halfExtent =
    toWorkspaceUnits(length) / 2 + toWorkspaceUnits(Math.max(length * ZOOM_SEGMENT_PADDING_FACTOR, MIN_SEGMENT_ZOOM_PADDING));

  MikeVisualizerLib.resetCameraToBounds([
    centerX - halfExtent,
    centerX + halfExtent,
    centerY - halfExtent,
    centerY + halfExtent,
    0,
    0,
  ] as IViewerBounds);

  store.dispatch({ type: EWorkspaceMeshActionType.CHECK_VIEW_BOUNDS, data: { workspaceId: workspace.id, wait: null } });
};
